import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Download, Crop, Languages, Trash2, AlertTriangle, ArrowRight, Plus, History } from "lucide-react";
import { api, media, API } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useAuth, useConfig, can } from "@/lib/auth";
import { fmtPrice, vTitle, fmtDate } from "@/lib/format";
import { PageTitle, Loader, Empty, Section, Field, RestrictionToggles } from "@/components/xd/ui";
import { SpecsForm, specsPayload, pickSpecs, PhotoUploader } from "@/components/xd/VehicleForm";
import { ReviewTag, AvailabilitySelect } from "@/pages/dealer/Dealer";
import { useAct, ActionDialog } from "./Admin";
import { AdminVehicleRow, DealerSelect } from "./AdminManage";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function AdminVehicles() {
  const { t } = useLang();
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "queue";
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  useEffect(() => {
    const params = tab === "queue" ? { review: "queue" } : tab === "sold" ? { availability: "sold" } : tab === "draft" ? { review: "draft" } : {};
    if (q) params.q = q;
    const id = setTimeout(() => api.get("/admin/vehicles", { params }).then((r) => setItems(r.data.items)).catch(() => setItems([])), 200);
    return () => { clearTimeout(id); };
  }, [tab, q]);
  return (
    <div>
      <PageTitle eyebrow={t("admin.catalog")} title={t("admin.vehicles")}>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Input placeholder={t("filter.search")} className="xd-input w-full sm:w-56" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-vehicle-search" />
          {can(user, "vehicles.manage") && <Link to="/admin/vehicles/new" className="btn-xd" data-testid="admin-add-vehicle-button"><Plus size={16} />{t("mgmt.new_vehicle")}</Link>}
        </div>
      </PageTitle>
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })} className="mb-6">
        <TabsList className="bg-[#0b0b0b] border border-white/10 flex-wrap h-auto">
          {["queue", "all", "draft", "sold"].map((s) => <TabsTrigger key={s} value={s} data-testid={`vehicles-tab-${s}`}>{t(`vtab.${s}`)}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {!items ? <Loader /> : !items.length ? <Empty text={t("common.none")} /> : (
        <div className="grid gap-3">{items.map((v) => <AdminVehicleRow key={v.id} v={v} />)}</div>
      )}
    </div>
  );
}

const CropDialog = ({ p, onDone }) => {
  const { t } = useLang();
  const act = useAct();
  const [box, setBox] = useState(p.crop || { left: 0, top: 0, right: 0, bottom: 0 });
  const [open, setOpen] = useState(false);
  const save = async () => { const r = await act(() => api.post(`/admin/photos/${p.id}/crop`, box)); if (r) { setOpen(false); onDone(); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button type="button" className="h-8 w-8 grid place-items-center border border-white/10 hover:border-white/40" title={t("photos.crop")} data-testid={`photo-crop-${p.id}`}><Crop size={14} /></button></DialogTrigger>
      <DialogContent className="bg-black border-white/10 text-white max-w-2xl" data-testid="crop-dialog">
        <DialogHeader><DialogTitle className="text-start">{t("photos.crop")}</DialogTitle></DialogHeader>
        <div className="relative bg-[#111] overflow-hidden" dir="ltr">
          <img src={`${API}/media/${p.id}/thumb`} alt="" className="w-full" />
          <div className="absolute border-2 border-[var(--xd-red)] shadow-[0_0_0_9999px_rgba(0,0,0,0.6)]" style={{ left: `${box.left}%`, top: `${box.top}%`, right: `${box.right}%`, bottom: `${box.bottom}%` }} />
        </div>
        <p className="text-xs text-zinc-500">{t("photos.crop_note")}</p>
        {["top", "bottom", "right", "left"].map((k) => (
          <div key={k} className="flex items-center gap-4"><span className="w-16 text-xs text-zinc-400">{t(`crop.${k}`)}</span>
            <Slider dir="ltr" min={0} max={45} step={1} value={[box[k]]} onValueChange={([x]) => setBox({ ...box, [k]: x })} data-testid={`crop-${k}`} /><span className="w-10 text-xs ltr-num">{box[k]}%</span></div>
        ))}
        <button className="btn-xd" onClick={save} data-testid="crop-save">{t("common.save")}</button>
      </DialogContent>
    </Dialog>
  );
};

const PhotoTile = ({ p, i, reload, canPublish, canManage, bust }) => {
  const { t } = useLang();
  const act = useAct();
  const review = (action, reason) => act(() => api.post(`/admin/photos/${p.id}/review`, { action, reason }), false).then(reload);
  const cls = p.status === "approved" ? "text-emerald-400" : p.status === "rejected" ? "text-red-400" : "text-amber-300";
  return (
    <div className="xd-panel" data-testid={`admin-photo-${i}`}>
      <img src={`${media(p.id, "thumb")}?v=${bust}`} alt="" className="aspect-[4/3] w-full object-cover" />
      <div className="p-2 space-y-2">
        <div className="flex items-center justify-between text-xs"><span className={cls}>{t(`photo.${p.status}`)}</span><span className="text-zinc-600 ltr-num">{p.original_type?.split("/")[1]} · {(p.original_size / 1048576).toFixed(1)}MB</span></div>
        {p.reject_reason && <p className="text-xs text-red-400">{p.reject_reason}</p>}
        <div className="flex flex-wrap gap-1.5">
          {canPublish && p.status !== "approved" && <button type="button" className="px-2 h-8 text-xs border border-emerald-700 text-emerald-300" onClick={() => review("approve")} data-testid={`photo-approve-${i}`}>{t("photos.approve")}</button>}
          {canPublish && p.status !== "rejected" && <ActionDialog testid={`photo-reject-${i}`} title={t("photos.reject")} label={t("common.reason")} onSubmit={(x) => review("reject", x)} trigger={<button type="button" className="px-2 h-8 text-xs border border-red-700 text-red-300" data-testid={`photo-reject-button-${i}`}>{t("photos.reject")}</button>} />}
          {canPublish && <CropDialog p={p} onDone={reload} />}
          {canPublish && (
            <Select value={p.watermark_corner} onValueChange={(c) => act(() => api.post(`/admin/photos/${p.id}/watermark`, { corner: c })).then(reload)}>
              <SelectTrigger className="h-8 w-16 text-xs px-2" data-testid={`photo-watermark-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>{["br", "bl", "tr", "tl"].map((c) => <SelectItem key={c} value={c}>{t(`corner.${c}`)}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <a href={`${API}/media/${p.id}/original?download=true`} className="h-8 w-8 grid place-items-center border border-white/10 hover:border-white/40" title={t("broker.download_original")} data-testid={`photo-original-${i}`}><Download size={14} /></a>
          {canManage && <button type="button" className="h-8 w-8 grid place-items-center border border-white/10 hover:border-red-500 text-zinc-500" onClick={() => act(() => api.delete(`/admin/photos/${p.id}`)).then(reload)} data-testid={`photo-delete-${i}`}><Trash2 size={14} /></button>}
        </div>
      </div>
    </div>
  );
};

const PriceBox = ({ letter, title, hint, value, tone, testid, children }) => (
  <div className={`xd-panel p-4 space-y-2 min-w-0 border-t-2 ${tone}`} data-testid={testid}>
    <p className="text-xs text-zinc-400"><span className="ltr-num font-semibold text-white inline-block mx-1.5">{letter}</span>{title}</p>
    <p className="ltr-num text-xl">{fmtPrice(value)}</p>
    <p className="text-[11px] text-zinc-500 leading-relaxed">{hint}</p>
    {children}
  </div>
);

const PriceHistory = ({ vid }) => {
  const { t } = useLang();
  const [h, setH] = useState(null);
  useEffect(() => { api.get(`/admin/vehicles/${vid}/history`).then((r) => setH(r.data.prices || [])).catch(() => setH([])); }, [vid]);
  if (!h?.length) return null;
  return (
    <div className="space-y-2" data-testid="price-history">
      <p className="text-xs text-zinc-400 flex items-center gap-2"><History size={13} />{t("price.history")}</p>
      <div className="overflow-x-auto"><table className="xd-table text-xs"><thead><tr><th>{t("common.date")}</th><th>{t("audit.actor")}</th><th>A</th><th>B</th></tr></thead>
        <tbody>{h.map((r, i) => (
          <tr key={i}><td className="whitespace-nowrap"><span className="ltr-num">{fmtDate(r.at)}</span></td><td><span className="ltr-num">{r.by}</span></td>
            <td className="whitespace-nowrap"><span className="ltr-num">{fmtPrice(r.from?.asking_price)} → {fmtPrice(r.to?.asking_price)}</span></td>
            <td className="whitespace-nowrap"><span className="ltr-num">{fmtPrice(r.from?.final_price)} → {fmtPrice(r.to?.final_price)}</span></td></tr>
        ))}</tbody></table></div>
    </div>
  );
};

const ConfEdit = ({ v, keys, onChange, testid }) => {
  const { t } = useLang();
  const act = useAct();
  const [f, setF] = useState(Object.fromEntries(keys.map((k) => [k, v[k] ?? ""])));
  const save = async () => {
    const body = Object.fromEntries(keys.map((k) => [k, f[k] === "" ? null : k.endsWith("price") ? Number(f[k]) : f[k]]));
    const r = await act(() => api.put(`/admin/vehicles/${v.id}/conf`, body));
    if (r) onChange(r.data);
  };
  return (
    <div className="flex flex-wrap items-end gap-3">
      {keys.map((k) => <Field key={k} label={t(`conf.${k}`)}><Input dir="ltr" type={k.endsWith("price") ? "number" : "text"} className="xd-input w-full sm:w-44" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`${testid}-${k}`} /></Field>)}
      <button type="button" className="btn-ghost" onClick={save} data-testid={`${testid}-save`}>{t("common.save")}</button>
    </div>
  );
};

const PriceCard = ({ v, onChange }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const act = useAct();
  const [price, setPrice] = useState(v.advertised_price || "");
  const fin = can(user, "financial.view");
  return (
    <Section title={t("admin.pricing")} testid="admin-price-section">
      {v.price_alert && <p className="text-sm text-[var(--xd-red)] flex items-center gap-2" data-testid="price-alert-banner"><AlertTriangle size={15} />{t("admin.price_alert")}</p>}
      <div className="grid sm:grid-cols-3 gap-3">
        {fin && <PriceBox letter="A" title={t("price.a_title")} hint={t("price.a_hint")} value={v.asking_price} tone="border-t-zinc-500" testid="admin-asking-price" />}
        {fin && <PriceBox letter="B" title={t("price.b_title")} hint={t("price.b_hint")} value={v.final_price} tone="border-t-amber-400" testid="admin-final-price" />}
        <PriceBox letter="C" title={t("price.c_title")} hint={t("price.c_hint")} value={v.advertised_price} tone="border-t-[var(--xd-red)]" testid="admin-public-price">
          {fin && v.final_price && v.advertised_price && <p className="text-[11px] ltr-num text-emerald-300" data-testid="admin-margin">C − B = {fmtPrice(v.advertised_price - v.final_price)}</p>}
        </PriceBox>
      </div>
      {!fin && <p className="text-xs text-zinc-500" data-testid="conf-prices-hidden">{t("price.hidden_no_perm")}</p>}
      {v.availability === "sold" && <p className="text-sm">{t("vehicle.last_price")}: <span className="ltr-num">{fmtPrice(v.last_advertised_price)}</span></p>}
      {can(user, "prices.set") && (
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t("price.c_set")}><Input type="number" dir="ltr" className="xd-input w-full sm:w-48" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="admin-advertised-price-input" /></Field>
          <ActionDialog testid="admin-price-confirm" title={t("price.c_confirm", { p: fmtPrice(price) })} onSubmit={async () => { const r = await act(() => api.put(`/admin/vehicles/${v.id}/price`, { advertised_price: Number(price) })); if (r) onChange(r.data); }}
            trigger={<button type="button" className="btn-xd" disabled={!price} data-testid="admin-save-price-button">{t("admin.set_price")}</button>} />
        </div>
      )}
      {fin && <ConfEdit v={v} keys={["asking_price", "final_price"]} onChange={onChange} testid="admin-conf-price" />}
      {fin && <PriceHistory vid={v.id} key={`${v.asking_price}-${v.final_price}-${v.updated_at}`} />}
    </Section>
  );
};

const DetailsEditor = ({ v, onChange }) => {
  const { t } = useLang();
  const { categories } = useConfig();
  const act = useAct();
  const [specs, setSpecs] = useState(pickSpecs(v));
  const [restr, setRestr] = useState(v.restrictions);
  const [cats, setCats] = useState(v.category_ids || []);
  const [featured, setFeatured] = useState(!!v.featured);
  const translate = async (source) => {
    const r = await act(() => api.post("/admin/translate", { source, fields: { title: specs[`title_${source}`], desc: specs[`desc_${source}`] } }), false);
    if (r) setSpecs({ ...specs, [`title_${r.data.target}`]: r.data.fields.title || "", [`desc_${r.data.target}`]: r.data.fields.desc || "" });
  };
  const save = async () => { const r = await act(() => api.put(`/admin/vehicles/${v.id}`, { specs: specsPayload(specs), restrictions: restr, category_ids: cats, featured })); if (r) onChange(r.data); };
  const tr = (s, k) => <button type="button" className="btn-ghost !min-h-[2.25rem] text-xs" onClick={() => translate(s)} data-testid={`translate-${s}-button`}><Languages size={14} />{t(k)}</button>;
  return (
    <>
      <Section title={t("dealer.specs")} testid="admin-specs-section">
        <SpecsForm value={specs} onChange={setSpecs} textActions={<div className="flex gap-2">{tr("he", "translate.he_ar")}{tr("ar", "translate.ar_he")}</div>} />
      </Section>
      <Section title={t("admin.listing")}>
        <div className="flex flex-wrap gap-2">{categories.map((c) => (
          <label key={c.id} className="flex items-center gap-2 xd-panel px-3 py-2 text-sm"><Checkbox checked={cats.includes(c.id)} onCheckedChange={(x) => setCats(x ? [...cats, c.id] : cats.filter((y) => y !== c.id))} data-testid={`cat-${c.slug}`} />{c.name_he} · {c.name_ar}</label>
        ))}</div>
        <label className="flex items-center gap-3 text-sm"><Switch checked={featured} onCheckedChange={setFeatured} data-testid="featured-switch" />{t("admin.featured")}</label>
      </Section>
      <Section title={t("broker.restrictions")}><RestrictionToggles value={restr} onChange={setRestr} /></Section>
      <button className="btn-xd" onClick={save} data-testid="admin-save-details-button">{t("admin.save_details")}</button>
    </>
  );
};

const LANG_SUFFIX = { he: " · עברית", ar: " · العربية" };
const changeLabel = (t, k) => {
  const [, base, l] = k.match(/^(title|desc)_(he|ar)$/) || [];
  if (base) return `${t(base === "desc" ? "spec.description" : "spec.title")}${LANG_SUFFIX[l]}`;
  return t(`spec.${k}`) === `spec.${k}` ? k : t(`spec.${k}`);
};

const PendingChanges = ({ changes }) => {
  const { t } = useLang();
  if (!changes) return null;
  return (
    <Section title={t("admin.pending_changes")} testid="pending-changes">
      <div className="grid sm:grid-cols-2 gap-2 text-sm">{Object.entries(changes).map(([k, val]) => <p key={k}><span className="text-zinc-500">{changeLabel(t, k)}:</span> <span className="text-amber-200">{String(val).slice(0, 160)}</span></p>)}</div>
    </Section>
  );
};

const ReassignDealer = ({ v, onChange }) => {
  const { t } = useLang();
  const act = useAct();
  const [d, setD] = useState(v.dealer_id);
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="w-full sm:w-80"><DealerSelect value={d} onChange={setD} testid="admin-reassign-select" /></div>
      <ActionDialog testid="admin-reassign" title={t("mgmt.reassign_confirm")} onSubmit={async () => { const r = await act(() => api.put(`/admin/vehicles/${v.id}/dealer`, { dealer_id: d })); if (r) onChange(r.data); }}
        trigger={<button type="button" className="btn-ghost" disabled={d === v.dealer_id} data-testid="admin-reassign-button">{t("mgmt.reassign_button")}</button>} />
    </div>
  );
};

export function AdminVehicleEdit() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const act = useAct();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [bust, setBust] = useState(0);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/vehicles/${id}`).then((r) => { setV(r.data); setBust(Date.now()); }).catch(() => navigate("/admin/vehicles"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  const loaded = !!v;
  useEffect(() => {
    const h = window.location.hash.slice(1);
    if (loaded && h) { setTimeout(() => document.getElementById(h)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150); }
  }, [loaded]);
  if (!v) return <Loader />;
  const pub = can(user, "vehicles.publish");
  const man = can(user, "vehicles.manage");
  const upload = async (files) => {
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    await act(() => api.post(`/admin/vehicles/${v.id}/photos`, fd));
    setBusy(false);
    load();
  };
  const call = async (fn) => { const r = await act(fn); if (r?.data?.id) { setV(r.data); setBust(Date.now()); } };
  return (
    <div className="space-y-6" key={v.updated_at}>
      <Link to="/admin/vehicles" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white"><ArrowRight size={16} />{t("admin.vehicles")}</Link>
      <div id="review" className="scroll-mt-20"><PageTitle eyebrow={<>{v.code} · {can(user, "dealers.manage") ? <Link to={`/admin/dealers/${v.dealer_id}`} className="underline hover:text-white" data-testid="vehicle-dealer-link">{v.dealer_uid}</Link> : v.dealer_uid}</>} title={vTitle(v, lang)}>
        <div className="flex flex-wrap items-center gap-3">
          <ReviewTag s={v.review_status} />
          {man && <AvailabilitySelect value={v.availability} onChange={(a) => call(() => api.put(`/admin/vehicles/${v.id}/availability`, { availability: a }))} testid="admin-availability-select" />}
          {pub && (["pending", "in_review", "changes_pending", "rejected", "changes_rejected", "changes_requested"].includes(v.review_status) || (v.review_status === "draft" && user.is_super && v.created_by === user.uid)) && <button className="btn-xd" onClick={() => call(() => api.post(`/admin/vehicles/${v.id}/approve`))} data-testid="admin-approve-vehicle-button">{t("admin.approve_publish")}</button>}
          {pub && ["pending", "in_review", "changes_pending"].includes(v.review_status) && <ActionDialog testid="admin-request-changes" title={t("admin.request_changes")} label={t("admin.changes_needed")} onSubmit={(x) => call(() => api.post(`/admin/vehicles/${v.id}/request-changes`, { reason: x }))} trigger={<button className="btn-ghost" data-testid="admin-request-changes-button">{t("admin.request_changes")}</button>} />}
          {pub && ["pending", "in_review", "changes_pending"].includes(v.review_status) && <ActionDialog testid="admin-reject-vehicle" title={t("admin.reject_vehicle")} label={t("common.reason")} onSubmit={(x) => call(() => api.post(`/admin/vehicles/${v.id}/reject`, { reason: x }))} trigger={<button className="btn-ghost" data-testid="admin-reject-vehicle-button">{t("users.reject")}</button>} />}
          {pub && !["pending", "in_review", "draft"].includes(v.review_status) && (
            <label className="flex items-center gap-2 text-sm"><Switch checked={v.published} onCheckedChange={(c) => call(() => api.put(`/admin/vehicles/${v.id}/publish`, { published: c }))} data-testid="admin-publish-switch" />{t("dealer.live")}</label>
          )}
          {man && <ActionDialog testid="admin-delete-vehicle" title={t("admin.delete_vehicle")} onSubmit={async () => { await act(() => api.delete(`/admin/vehicles/${v.id}`)); navigate("/admin/vehicles"); }} trigger={<button className="btn-ghost" data-testid="admin-delete-vehicle-button"><Trash2 size={16} /></button>} />}
        </div>
      </PageTitle></div>
      {v.rejection_reason && <p className="text-sm text-red-400">{t(v.review_status === "changes_requested" ? "dealer.admin_feedback" : "dealer.rejected_reason")}: {v.rejection_reason}</p>}
      <PendingChanges changes={v.pending_changes} />
      {v.review_status === "draft" && <p className="text-sm text-amber-300 border border-amber-500/40 bg-amber-950/20 p-3" data-testid="admin-draft-note">{t("mgmt.draft_note")}</p>}
      <div id="price" className="scroll-mt-20"><PriceCard v={v} onChange={setV} /></div>
      {v.contact_name !== undefined && (
        <Section title={t("admin.dealer_confidential")} testid="admin-dealer-confidential">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <p><span className="text-zinc-500">{t("form.business_name")}: </span>{v.dealer_business_name}</p>
            <p><span className="text-zinc-500">{t("profile.account_phone")}: </span><span className="ltr-num">{v.dealer_phone}</span></p>
          </div>
          <p className="text-xs text-zinc-500">{v.contact_override ? t("profile.override_active") : t("profile.from_profile")}</p>
          <ConfEdit v={v} keys={["contact_name", "contact_phone"]} onChange={setV} testid="admin-conf-contact" />
        </Section>
      )}
      {user.is_super && (
        <Section title={t("mgmt.reassign")} testid="admin-reassign-section">
          <ReassignDealer v={v} onChange={setV} />
        </Section>
      )}
      <div id="photos" className="scroll-mt-20"><Section title={t("photos.title", { n: v.photos.length })} actions={man && <PhotoUploader onFiles={upload} busy={busy} testid="admin-photo-upload-input" />} testid="admin-photos-section">
        <p className="text-xs text-zinc-500">{t("photos.moderation_note")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {v.photos.map((p, i) => <PhotoTile key={p.id} p={p} i={i} reload={load} canPublish={pub} canManage={man} bust={bust} />)}
        </div>
      </Section></div>
      {man ? <DetailsEditor v={v} onChange={setV} /> : null}
      <p className="text-xs text-zinc-600 ltr-num">{fmtDate(v.created_at)} · {fmtDate(v.updated_at)}</p>
    </div>
  );
}
